import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, ClipboardCheck, ListChecks, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { AssigneeSelect, CategorySelect, EquipmentSelect, InspectionStatusBadge, PlaceFields, ScoreBadge } from '../../components/facilities-ui';
import { useToast } from '../../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Pagination,
  Select,
  Spinner,
  Switch,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useDesk } from '../../lib/desk';
import {
  equipmentLabel,
  INSPECTION_KIND_LABEL,
  placeLabel,
  useFacilityPlaces,
  useInspectionTemplates,
  type InspectionDetail,
  type InspectionKind,
  type InspectionListItem,
  type InspectionTemplate,
  type InspectionTemplateInput,
} from '../../lib/facilities';
import { formatDate, formatUtcDate } from '../../lib/format';
import { useDebouncedValue, useUrlState } from '../../lib/hooks';
import type { Page } from '../../lib/types';

const PAGE_SIZE = 25;
const MAX_ITEMS = 200;
const DEFAULTS = { tab: 'inspections', state: 'open', search: '', buildingId: '', kind: '', mine: '', page: '1' };

type PageTab = 'inspections' | 'templates';
type StateFilter = 'open' | 'completed' | 'canceled' | 'all';
const STATE_FILTERS: StateFilter[] = ['open', 'completed', 'canceled', 'all'];
const KINDS = Object.keys(INSPECTION_KIND_LABEL) as InspectionKind[];

const plural = (count: number, word: string) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

function InspectionProgress({ inspection }: { inspection: InspectionListItem }) {
  if (inspection.status === 'COMPLETED') {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <ScoreBadge score={inspection.score} />
        <span className={cn('text-xs', inspection.failedCount > 0 ? 'font-medium text-red-700' : 'text-slate-500')}>
          {inspection.failedCount.toLocaleString()} failed
        </span>
        <span className="text-xs text-slate-500">{plural(inspection.followUpCount, 'follow-up')}</span>
      </div>
    );
  }
  const percent = inspection.itemCount ? Math.round((inspection.answeredCount * 100) / inspection.itemCount) : 0;
  return (
    <div className="w-36">
      <p className="text-xs text-slate-600">
        {inspection.answeredCount.toLocaleString()} of {inspection.itemCount.toLocaleString()}
      </p>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div className={cn('h-full rounded-full', inspection.status === 'CANCELED' ? 'bg-slate-400' : 'bg-brand-600')} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function InspectionsTab() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const places = useFacilityPlaces();

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const state: StateFilter = (STATE_FILTERS as string[]).includes(filters.state) ? (filters.state as StateFilter) : 'open';
  const page = Number(filters.page) || 1;
  const mine = !!filters.mine;
  const filtered = !!(filters.search || filters.buildingId || filters.kind || mine);

  const list = useQuery({
    queryKey: ['facilities', 'inspections', { state, search: filters.search, buildingId: filters.buildingId, kind: filters.kind, mine, page }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<Page<InspectionListItem>>('/facilities/inspections', {
        state,
        search: filters.search,
        buildingId: filters.buildingId,
        kind: filters.kind,
        inspectorId: mine ? 'me' : undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  return (
    <Card>
      <Tabs<StateFilter>
        className="px-3 pt-1"
        value={state}
        onChange={(value) => setFilters({ state: value })}
        tabs={[
          { value: 'open', label: 'Open' },
          { value: 'completed', label: 'Completed' },
          { value: 'canceled', label: 'Canceled' },
          { value: 'all', label: 'All' },
        ]}
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, building, room, equipment"
          aria-label="Search inspections"
          className="w-full sm:w-72"
        />
        <Select value={filters.buildingId} onChange={(e) => setFilters({ buildingId: e.target.value })} className="w-auto" aria-label="Building">
          <option value="">Any building</option>
          {places.data?.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
        </Select>
        <Select value={filters.kind} onChange={(e) => setFilters({ kind: e.target.value })} className="w-auto" aria-label="Kind of inspection">
          <option value="">Any kind</option>
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {INSPECTION_KIND_LABEL[kind]}
            </option>
          ))}
        </Select>
        <label className="flex h-9 items-center gap-2 px-1 text-sm text-slate-700">
          <input type="checkbox" checked={mine} onChange={(e) => setFilters({ mine: e.target.checked ? '1' : '' })} className="size-4 rounded border-slate-300" />
          Assigned to me
        </label>
        {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
      </div>

      {list.isLoading ? (
        <PageLoader />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data!.items.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck />}
          title={filtered ? 'No inspections match these filters' : state === 'open' ? 'No open inspections' : 'No inspections here yet'}
          description={!filtered && state === 'open' ? 'Start one from a template, or add an inspection schedule to open them automatically.' : undefined}
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Inspection</Th>
                <Th className="hidden md:table-cell">Where</Th>
                <Th className="hidden lg:table-cell">Inspector</Th>
                <Th>Due</Th>
                <Th className="hidden sm:table-cell">Progress</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {list.data!.items.map((inspection) => {
                const where = placeLabel(inspection);
                return (
                  <tr key={inspection.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/facilities/inspections/${inspection.id}`)}>
                    <Td className="max-w-xs">
                      <Link
                        to={`/facilities/inspections/${inspection.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate font-medium text-slate-900 hover:text-brand-700"
                      >
                        {inspection.name}
                      </Link>
                      <p className="truncate text-xs text-slate-500">
                        {INSPECTION_KIND_LABEL[inspection.kind]}
                        {inspection.schedule && ' · From a schedule'}
                      </p>
                    </Td>
                    <Td className="hidden max-w-[16rem] md:table-cell">
                      {where || inspection.facilityAsset ? (
                        <>
                          {where && <p className="truncate text-slate-800">{where}</p>}
                          {inspection.facilityAsset && <p className="truncate text-xs text-slate-500">{equipmentLabel(inspection.facilityAsset)}</p>}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </Td>
                    <Td className="hidden whitespace-nowrap lg:table-cell">
                      {inspection.inspector?.name ?? <span className="text-slate-400">Unassigned</span>}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className={inspection.overdue ? 'font-medium text-red-600' : 'text-slate-600'}>{formatUtcDate(inspection.dueOn)}</span>
                      {inspection.completedAt && <p className="text-xs text-slate-500">Done {formatDate(inspection.completedAt)}</p>}
                    </Td>
                    <Td className="hidden sm:table-cell">
                      <InspectionProgress inspection={inspection} />
                    </Td>
                    <Td>
                      <InspectionStatusBadge status={inspection.status} overdue={inspection.overdue} />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={PAGE_SIZE} total={list.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
        </>
      )}
    </Card>
  );
}

const BLANK_INSPECTION = { templateId: '', name: '', buildingId: '', roomId: '', facilityAssetId: '', inspectorId: '', dueOn: '' };

function NewInspectionModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const templates = useInspectionTemplates(false, open);
  const [form, setForm] = useState(BLANK_INSPECTION);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(BLANK_INSPECTION);
    setError('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.post<InspectionDetail>('/facilities/inspections', {
        templateId: form.templateId,
        name: form.name.trim() || null,
        buildingId: form.buildingId || null,
        roomId: form.roomId || null,
        facilityAssetId: form.facilityAssetId || null,
        inspectorId: form.inspectorId || null,
        dueOn: form.dueOn || null,
      }),
    onSuccess: (inspection) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Inspection created');
      onClose();
      navigate(`/facilities/inspections/${inspection.id}`);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const template = templates.data?.find((t) => t.id === form.templateId);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    create.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New inspection"
      description="The inspection starts with the template’s checklist. Later changes to the template don’t affect it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="new-inspection-form" loading={create.isPending} disabled={!form.templateId}>
            Create inspection
          </Button>
        </>
      }
    >
      <form id="new-inspection-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        {templates.data?.length === 0 && <Alert tone="amber">There are no inspection templates yet. A facilities admin can add one on the Templates tab.</Alert>}
        <Field
          label="Template"
          required
          hint={template ? [plural(template.items.length, 'item'), INSPECTION_KIND_LABEL[template.kind], template.description].filter(Boolean).join(' · ') : undefined}
        >
          <Select value={form.templateId} required autoFocus onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
            <option value="">{templates.isLoading ? 'Loading templates…' : 'Choose a template'}</option>
            {templates.data?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Name" hint="Leave empty to use the template’s name">
          <Input value={form.name} maxLength={150} placeholder={template?.name ?? 'e.g. Main Hall walkthrough'} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <PlaceFields
            buildingId={form.buildingId}
            roomId={form.roomId}
            onChange={(place) => setForm({ ...form, ...place, facilityAssetId: place.buildingId === form.buildingId ? form.facilityAssetId : '' })}
          />
        </div>
        <Field label="Equipment" hint={form.buildingId ? 'Equipment in the chosen building' : undefined}>
          <EquipmentSelect value={form.facilityAssetId} buildingId={form.buildingId || undefined} onChange={(facilityAssetId) => setForm({ ...form, facilityAssetId })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Inspector" hint="They’ll get a notification">
            <AssigneeSelect value={form.inspectorId} emptyLabel="Nobody yet" onChange={(inspectorId) => setForm({ ...form, inspectorId })} />
          </Field>
          <Field label="Due">
            <Input type="date" value={form.dueOn} onChange={(e) => setForm({ ...form, dueOn: e.target.value })} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

type ItemDraft = { key: string; id?: string; section: string; label: string; guidance: string };
type TemplateDraft = {
  id?: string;
  name: string;
  kind: InspectionKind;
  description: string;
  createFollowUps: boolean;
  followUpDepartmentId: string;
  archived: boolean;
  inUse: boolean;
  items: ItemDraft[];
};

let nextItemKey = 0;
const newItem = (section = ''): ItemDraft => ({ key: `new-${nextItemKey++}`, section, label: '', guidance: '' });

const blankTemplate = (): TemplateDraft => ({
  name: '',
  kind: 'OTHER',
  description: '',
  createFollowUps: true,
  followUpDepartmentId: '',
  archived: false,
  inUse: false,
  items: [newItem()],
});

// A follow-up category that no longer exists (followUpCategory is null) is dropped, so saving doesn't fail.
const liveCategory = (template: InspectionTemplate) => (template.followUpCategory ? template.followUpDepartmentId : null);

const templateDraft = (template: InspectionTemplate): TemplateDraft => ({
  id: template.id,
  name: template.name,
  kind: template.kind,
  description: template.description ?? '',
  createFollowUps: template.createFollowUps,
  followUpDepartmentId: liveCategory(template) ?? '',
  archived: template.archived,
  inUse: template.inspectionCount + template.scheduleCount > 0,
  items: template.items.map((item) => ({ key: item.id, id: item.id, section: item.section, label: item.label, guidance: item.guidance ?? '' })),
});

/** The template as the API takes it, unchanged. */
const templateInput = (template: InspectionTemplate): InspectionTemplateInput => ({
  name: template.name,
  kind: template.kind,
  description: template.description,
  items: template.items.map(({ id, section, label, guidance }) => ({ id, section, label, guidance })),
  createFollowUps: template.createFollowUps,
  followUpDepartmentId: liveCategory(template),
  archived: template.archived,
});

function TemplateModal({ draft, onClose }: { draft: TemplateDraft | null; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TemplateDraft | null>(draft);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(draft);
    setError('');
  }, [draft]);

  const save = useMutation({
    mutationFn: ({ id, ...values }: TemplateDraft) => {
      const body: InspectionTemplateInput = {
        name: values.name.trim(),
        kind: values.kind,
        description: values.description.trim() || null,
        // Existing items keep their ids so past results still line up; new ones get an id from the server.
        items: values.items.map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          section: item.section.trim(),
          label: item.label.trim(),
          guidance: item.guidance.trim() || null,
        })),
        createFollowUps: values.createFollowUps,
        followUpDepartmentId: values.createFollowUps ? values.followUpDepartmentId || null : null,
        archived: values.archived,
      };
      return id ? api.put<InspectionTemplate>(`/facilities/inspection-templates/${id}`, body) : api.post<InspectionTemplate>('/facilities/inspection-templates', body);
    },
    onSuccess: (_, values) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(values.id ? 'Template saved' : 'Template added');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!form) return null;

  const setItem = (index: number, patch: Partial<ItemDraft>) => setForm({ ...form, items: form.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  const moveItem = (index: number, offset: number) => {
    const items = [...form.items];
    const [item] = items.splice(index, 1);
    items.splice(index + offset, 0, item);
    setForm({ ...form, items });
  };
  const removeItem = (index: number) => setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  const addItem = () => setForm({ ...form, items: [...form.items, newItem(form.items[form.items.length - 1]?.section ?? '')] });
  const sections = [...new Set(form.items.map((item) => item.section.trim()).filter(Boolean))];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    save.mutate(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={form.id ? `Edit ${draft?.name ?? 'template'}` : 'New inspection template'}
      description={
        form.inUse
          ? 'Changes apply to inspections opened from now on. Inspections already opened keep the checklist they started with.'
          : 'The checklist inspectors work through. Group items into sections, such as each area of a building.'
      }
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="inspection-template-form" loading={save.isPending}>
            Save template
          </Button>
        </>
      }
    >
      <form id="inspection-template-form" onSubmit={submit} className="space-y-5">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={form.name} required maxLength={120} autoFocus placeholder="e.g. Monthly playground inspection" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Kind">
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as InspectionKind })}>
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {INSPECTION_KIND_LABEL[kind]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Description">
          <Textarea
            rows={2}
            maxLength={2000}
            value={form.description}
            placeholder="What this inspection covers and when to do it"
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <div className="space-y-3 rounded-lg border border-slate-200 p-4">
          <Switch
            checked={form.createFollowUps}
            onChange={(createFollowUps) => setForm({ ...form, createFollowUps })}
            label="Open follow-up work orders"
            description="When an inspection is completed, each failed item becomes a facilities work order that includes the inspector’s note."
          />
          {form.createFollowUps && (
            <Field label="Category for follow-ups">
              <CategorySelect value={form.followUpDepartmentId} onChange={(followUpDepartmentId) => setForm({ ...form, followUpDepartmentId })} />
            </Field>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Checklist</p>
              <p className="text-xs text-slate-500">Inspectors mark each item pass, fail, or not applicable.</p>
            </div>
            <span className="text-xs whitespace-nowrap text-slate-500">{plural(form.items.length, 'item')}</span>
          </div>
          <ol className="space-y-2">
            {form.items.map((item, index) => (
              <li key={item.key} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                <div className="flex items-start gap-2">
                  <span className="mt-2 w-6 shrink-0 text-right text-xs font-medium text-slate-400">{index + 1}</span>
                  <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
                    <Input
                      value={item.section}
                      maxLength={100}
                      list="inspection-template-sections"
                      placeholder="Section"
                      aria-label={`Item ${index + 1} section`}
                      onChange={(e) => setItem(index, { section: e.target.value })}
                    />
                    <Input
                      value={item.label}
                      required
                      maxLength={300}
                      // New rows take focus when they're added; the first row of a new template leaves it on the name.
                      autoFocus={!item.id && form.items.length > 1}
                      placeholder="What to check, e.g. Exit signs are lit"
                      aria-label={`Item ${index + 1}`}
                      onChange={(e) => setItem(index, { label: e.target.value })}
                    />
                    <Input
                      className="sm:col-span-2"
                      value={item.guidance}
                      maxLength={1000}
                      placeholder="Guidance for the inspector (optional)"
                      aria-label={`Item ${index + 1} guidance`}
                      onChange={(e) => setItem(index, { guidance: e.target.value })}
                    />
                  </div>
                  <div className="flex shrink-0 flex-col gap-0.5 sm:flex-row">
                    <Button size="sm" variant="ghost" icon={<ArrowUp />} disabled={index === 0} onClick={() => moveItem(index, -1)} aria-label="Move up" title="Move up" />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<ArrowDown />}
                      disabled={index === form.items.length - 1}
                      onClick={() => moveItem(index, 1)}
                      aria-label="Move down"
                      title="Move down"
                    />
                    <Button size="sm" variant="ghost" icon={<Trash2 />} disabled={form.items.length === 1} onClick={() => removeItem(index)} aria-label="Remove" title="Remove" />
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <datalist id="inspection-template-sections">
            {sections.map((section) => (
              <option key={section} value={section} />
            ))}
          </datalist>
          <Button className="mt-2" size="sm" icon={<Plus />} disabled={form.items.length >= MAX_ITEMS} onClick={addItem}>
            Add item
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TemplatesTab({ onEdit }: { onEdit(draft: TemplateDraft): void }) {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const templates = useInspectionTemplates(true);
  const [deleting, setDeleting] = useState<InspectionTemplate | null>(null);

  const archive = useMutation({
    mutationFn: (template: InspectionTemplate) =>
      api.put<InspectionTemplate>(`/facilities/inspection-templates/${template.id}`, { ...templateInput(template), archived: !template.archived }),
    onSuccess: (_, template) => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success(template.archived ? `${template.name} restored` : `${template.name} archived`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/facilities/inspection-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      setDeleting(null);
      toast.success('Template deleted');
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  if (templates.isLoading) return <PageLoader />;
  if (templates.error) return <ErrorState error={templates.error} onRetry={() => templates.refetch()} />;
  const items = templates.data ?? [];

  return (
    <>
      {items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ListChecks />}
            title="No inspection templates yet"
            description="Templates are the checklists inspections are done from, such as a custodial walkthrough or a monthly playground check."
            action={
              canEdit && (
                <Button variant="primary" icon={<Plus />} onClick={() => onEdit(blankTemplate())}>
                  New template
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((template) => {
            const sectionCount = new Set(template.items.map((item) => item.section).filter(Boolean)).size;
            return (
              <Card key={template.id} className={cn('flex flex-col p-5', template.archived && 'bg-slate-50')}>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={cn('font-semibold', template.archived ? 'text-slate-600' : 'text-slate-900')}>{template.name}</p>
                      <p className="text-xs text-slate-500">{INSPECTION_KIND_LABEL[template.kind]}</p>
                    </div>
                    {template.archived && <Badge tone="amber">Archived</Badge>}
                  </div>
                  {template.description && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{template.description}</p>}
                  <ul className="mt-3 space-y-1.5 text-xs text-slate-600 [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-slate-400">
                    <li className="flex items-start gap-1.5">
                      <ListChecks className="mt-px" />
                      <span>
                        {plural(template.items.length, 'item')}
                        {sectionCount > 0 && ` in ${plural(sectionCount, 'section')}`}
                      </span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <Wrench className="mt-px" />
                      <span>
                        {template.createFollowUps ? (
                          <>
                            Opens a follow-up work order for each failed item
                            {template.followUpCategory && (
                              <>
                                {' '}
                                in <span className="font-medium text-slate-700">{template.followUpCategory}</span>
                              </>
                            )}
                          </>
                        ) : (
                          'No follow-ups'
                        )}
                      </span>
                    </li>
                    <li className="flex items-start gap-1.5">
                      <ClipboardCheck className="mt-px" />
                      <span>
                        Used by {plural(template.scheduleCount, 'schedule')} and {plural(template.inspectionCount, 'inspection')}
                      </span>
                    </li>
                  </ul>
                </div>
                {canEdit && (
                  <div className="mt-4 flex flex-wrap gap-1 border-t border-slate-100 pt-3">
                    <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => onEdit(templateDraft(template))}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={template.archived ? <ArchiveRestore /> : <Archive />}
                      loading={archive.isPending && archive.variables?.id === template.id}
                      onClick={() => archive.mutate(template)}
                    >
                      {template.archived ? 'Restore' : 'Archive'}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(template)}>
                      Delete
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete ${deleting?.name ?? 'this template'}?`}
        description="This can’t be undone. Templates that inspections or schedules use can’t be deleted; archive them instead so their records are kept."
        confirmLabel="Delete template"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InspectionsPage() {
  const desk = useDesk();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const tab: PageTab = filters.tab === 'templates' ? 'templates' : 'inspections';
  const [creating, setCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateDraft | null>(null);

  return (
    <>
      <PageHeader
        title="Inspections"
        description="Custodial walkthroughs, safety audits, and playground inspections, done from standard templates. Failed items can open follow-up work orders."
        actions={
          tab === 'inspections'
            ? desk.can('TECHNICIAN') && (
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  New inspection
                </Button>
              )
            : desk.can('ADMIN') && (
                <Button variant="primary" icon={<Plus />} onClick={() => setEditingTemplate(blankTemplate())}>
                  New template
                </Button>
              )
        }
      />

      <Tabs<PageTab>
        className="mb-4"
        value={tab}
        onChange={(value) => setFilters({ tab: value })}
        tabs={[
          { value: 'inspections', label: 'Inspections' },
          { value: 'templates', label: 'Templates' },
        ]}
      />

      {tab === 'inspections' ? <InspectionsTab /> : <TemplatesTab onEdit={setEditingTemplate} />}

      <NewInspectionModal open={creating} onClose={() => setCreating(false)} />
      <TemplateModal draft={editingTemplate} onClose={() => setEditingTemplate(null)} />
    </>
  );
}
